import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core-typeahead';
import '@maersk-global/mds-components-core-tag';
import '@maersk-global/mds-components-core-input';
import '@maersk-global/mds-components-core-button';
import { VendorSpecificSettingsApiResponse, VendorSpecificSettingsSummary } from '../../../models/vsc-settings.model';
import { VscKpiCategoryService } from '../../../services/vsc-kpi-category.service';
import { IMcTypeaheadData } from '@maersk-global/mds-components-core-typeahead/types';
import { AuthHelper } from '../../../auth/auth-helper';
import { Observable, tap } from 'rxjs';

@Component({
  selector: 'app-vsc-settings-profiles',
  imports: [CommonModule],
  templateUrl: './vsc-settings-profiles.component.html',
  styleUrl: './vsc-settings-profiles.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscSettingsProfilesComponent implements OnInit {
  @Output() profileSelect = new EventEmitter<VendorSpecificSettingsApiResponse>();
  @Output() createCopy = new EventEmitter<VendorSpecificSettingsApiResponse>();
  @Output() profileDeleted = new EventEmitter<number>();
  tagSearch = '';
  vendorSpecificSettingsSummary: VendorSpecificSettingsSummary[] = [];
  typeaheadData: IMcTypeaheadData[] = [];
  vendorSpecificSettingsApiResponse: VendorSpecificSettingsApiResponse[] = [];
  slectedSettingsId: number = 0;
  isLoading = false;
  readonly DUMMY_ID = -1;
  constructor(private readonly vscKpiService: VscKpiCategoryService) {}
  ngOnInit(): void {
    this.loadProfiles();
  }

  reload(selectId?: number): void {
    this.loadProfiles(selectId);
  }

  private loadProfiles(selectId?: number): void {
    this.typeaheadData = [];
    this.isLoading = true;
    this.vscKpiService.getVendorSettingsProfiles().subscribe((profiles) => {
      this.isLoading = false;
      this.vendorSpecificSettingsSummary = profiles.map((p) => ({
        id: p.id,
        tags: p.tags,
        shopCount: p.vendorIds.length,
        vendorCodes: p.vendorIds,
      }));
      this.vendorSpecificSettingsApiResponse = profiles;

      for (const profile of profiles) {
        this.typeaheadData.push(
          ...profile.tags.map((tag) => ({
            label: tag,
            value: profile.id,
          })),
          ...profile.vendorIds.map((shop) => ({
            label: shop,
            value: profile.id,
          })),
        );
      }

      if (selectId !== undefined) {
        this.slectedSettingsId = selectId;
      }
    });
  }

  get filteredProfiles(): VendorSpecificSettingsSummary[] {
    if (!this.tagSearch) {
      return this.vendorSpecificSettingsSummary;
    }
    return this.vendorSpecificSettingsSummary.filter((profile) => profile.id.toString() == this.tagSearch);
  }

  insertDummyCard(): void {
    this.removeDummyCard();
    this.vendorSpecificSettingsSummary = [
      ...this.vendorSpecificSettingsSummary,
      { id: this.DUMMY_ID, tags: [], shopCount: 0, vendorCodes: [] },
    ];
    this.slectedSettingsId = this.DUMMY_ID;
  }

  private removeDummyCard(): void {
    this.vendorSpecificSettingsSummary = this.vendorSpecificSettingsSummary.filter((p) => p.id !== this.DUMMY_ID);
  }

  toggleProfile(profileId: number): void {
    if (profileId === this.DUMMY_ID) return;
    this.removeDummyCard();
    this.slectedSettingsId = profileId;
    const profile = this.vendorSpecificSettingsApiResponse.find((p) => p.id === profileId);
    if (profile) {
      this.profileSelect.emit(profile);
    }
  }

  onOptionSelected(event: Event): void {
    const detail = (event as CustomEvent).detail as { value?: string };
    this.tagSearch = detail?.value ?? '';
  }

  clearSearch(): void {
    this.tagSearch = '';
  }
  onSearch(event: Event): void {
    const controlVal = (event.target as HTMLInputElement).value;
    if (!controlVal) {
      this.tagSearch = '';
    }
  }
  isSelected(profileId: number): boolean {
    return this.slectedSettingsId === profileId;
  }

  get isSelectedDefault(): boolean {
    const profile = this.vendorSpecificSettingsApiResponse.find((p) => p.id === this.slectedSettingsId);
    return profile?.tags.includes('default') ?? false;
  }

  deleteConfirm = (): Observable<void> => {
    const user = AuthHelper.user;
    const deletedId = this.slectedSettingsId;
    return this.vscKpiService
      .deleteVendorSettings(deletedId, user?.userId ?? 0, new Date().toISOString())
      .pipe(tap(() => {
        this.slectedSettingsId = 0;
        this.profileDeleted.emit(deletedId);
        this.loadProfiles();
      }));
  };

  onCreateCopy(): void {
    const profile = this.vendorSpecificSettingsApiResponse.find((p) => p.id === this.slectedSettingsId);
    if (profile) {
      this.createCopy.emit(profile);
    }
  }
}
